import { useEffect, useState, type ReactNode } from "react";
import { useNavigate, useRouterState } from "@tanstack/react-router";
import { AppShell, type NavItem, Muted } from "@coachbrain/ui";
import { en } from "../i18n/en";
import { icons } from "./icons";
import { apiMode } from "../api";
import { clearSession, useSession } from "../lib/session";
import { useApi } from "../lib/hooks";

export function Loading() { return <p className="cb-muted" role="status" style={{ padding: 12 }}>{en.common.loading}</p>; }
export function ErrorBox({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return <div className="cb-card" role="alert"><span className="cb-error">{en.common.error}</span><span className="cb-small cb-muted">{message}</span>{onRetry ? <button type="button" className="cb-btn cb-btn-outline" onClick={onRetry}>{en.common.retry}</button> : null}</div>;
}

/** Theme toggle for the rail footer (explicit choice persists; "system" follows prefers-color-scheme). */
export function ThemeToggle() {
  const [theme, setTheme] = useState<string>(() => document.documentElement.dataset["theme"] ?? "system");
  const apply = (v: string) => { setTheme(v); try { if (v === "system") { delete document.documentElement.dataset["theme"]; localStorage.removeItem("cb-theme"); } else { document.documentElement.dataset["theme"] = v; localStorage.setItem("cb-theme", v); } } catch { /* ignore */ } };
  const next = theme === "dark" ? "light" : theme === "light" ? "system" : "dark";
  return <button type="button" className="cb-rail-theme" onClick={() => apply(next)} aria-label={`${en.settings.theme}: ${theme}`} title={`${en.settings.theme}: ${theme}`}><span className="cb-rail-icon">{theme === "dark" ? icons.moon : theme === "light" ? icons.sun : icons.auto}</span><span className="cb-rail-label">{en.settings.theme}: {theme}</span></button>;
}

/** Rail footer: who is signed in (from the session, never a fixture name) plus the theme toggle. */
function RailFooter({ name }: { name?: string }) {
  return <>{name ? <span className="cb-strong cb-rail-name">{name}</span> : null}<ThemeToggle /></>;
}
/** What the top-right corner of the chrome says: the signed-in user's name, or the app name when nobody is signed in. */
export function useDisplayName(): string {
  const me = useSession();
  return me?.user.name ?? en.app;
}

/** Upper-right account actions: a profile icon that opens the account screen, and a log-out icon beside it. */
export function TopActions({ profileHref }: { profileHref?: string }) {
  const navigate = useNavigate();
  const api = useApi();
  const logOut = async () => { try { await api?.logout(); } finally { clearSession(); void navigate({ to: "/login" }); } };
  return (
    <>
      {profileHref ? <button type="button" className="cb-icon-button" aria-label={en.nav.profile} title={en.nav.profile} onClick={() => void navigate({ to: profileHref })}>{icons.user}</button> : null}
      <button type="button" className="cb-icon-button" aria-label={en.nav.logOut} title={en.nav.logOut} onClick={() => void logOut()}>{icons.logout}</button>
    </>
  );
}
const brand = (text: string, mark = "CB") => <><span className="cb-rail-brand-mark" aria-hidden="true">{mark}</span><span className="cb-rail-brand-text">{text}</span></>;

export function CoachShell(props: { title?: ReactNode; subtitle?: ReactNode; right?: ReactNode; tabs?: ReactNode; children: ReactNode; wide?: boolean; hideNav?: boolean; narrow?: boolean }) {
  const navigate = useNavigate();
  const path = useRouterState({ select: (s) => s.location.pathname });
  const nav: NavItem[] = [
    { label: en.nav.clients, href: "/clients", active: path.startsWith("/clients"), icon: icons.clients },
    { label: en.nav.today, href: "/today", active: path.startsWith("/today"), icon: icons.today },
    { label: en.nav.log, href: "/log", active: path.startsWith("/log") || /\/clients\/[^/]+\/log/.test(path), icon: icons.log },
    { label: en.nav.library, href: "/library", active: path.startsWith("/library"), icon: icons.library },
    { label: en.settings.title, href: "/settings", active: path.startsWith("/settings"), icon: icons.settings },
  ];
  // The phone bottom nav keeps the design's four items; Settings lives under Library there.
  const phoneNav = nav.slice(0, 4).map((n, i) => (i === 3 ? { ...n, active: n.active || path.startsWith("/settings") } : n));
  const isDesktop = useMedia("(min-width: 768px)");
  const me = useSession();
  // Signed-out screens (login, signup) carry no navigation and no account actions: there is no account yet.
  const navItems = !me ? undefined : props.hideNav && !isDesktop ? undefined : (isDesktop ? nav : phoneNav);
  return (
    <AppShell title={props.title} subtitle={props.subtitle} right={props.right ?? (me?.user.name ?? en.app)} tabs={props.tabs} nav={navItems} hideNav={props.hideNav} wide={props.wide} narrow={props.narrow} onNavigate={(href) => void navigate({ to: href })} railBrand={brand(en.app)} railFooter={<RailFooter name={me?.user.name} />} actions={me ? <TopActions profileHref="/settings" /> : undefined}>
      {apiMode() === "mock" ? <Muted className="cb-small">{en.common.mockBanner}</Muted> : null}
      {props.children}
    </AppShell>
  );
}

export function TraineeShell(props: { title?: ReactNode; subtitle?: ReactNode; right?: ReactNode; children: ReactNode; chrome?: "navy" | "emerald"; narrow?: boolean }) {
  const navigate = useNavigate();
  const path = useRouterState({ select: (s) => s.location.pathname });
  const nav: NavItem[] = [
    { label: en.nav.home, href: "/t/home", active: path === "/t/home", icon: icons.home },
    { label: en.nav.reports, href: "/t/reports", active: path.startsWith("/t/reports"), icon: icons.reports },
    { label: en.nav.sessions, href: "/t/sessions", active: path.startsWith("/t/sessions"), icon: icons.sessions },
    { label: en.nav.coach, href: "/t/coach", active: path.startsWith("/t/coach"), icon: icons.coach },
  ];
  // The trainee's rail is branded with their coach's practice; the footer is the trainee themself.
  const me = useSession();
  const practiceName = me?.practice.name ?? en.app;
  return <AppShell title={props.title} subtitle={props.subtitle} right={props.right ?? (me?.user.name ?? en.app)} nav={nav} chrome={props.chrome} narrow={props.narrow} onNavigate={(href) => void navigate({ to: href })} railBrand={brand(practiceName, practiceName.replace(/^Coach\s+/i, "").slice(0, 1).toUpperCase() || "CB")} railFooter={<RailFooter name={me?.user.name} />} actions={me ? <TopActions profileHref="/t/coach" /> : undefined}>{props.children}</AppShell>;
}

export function AdminShell(props: { title: ReactNode; children: ReactNode }) {
  const navigate = useNavigate();
  const path = useRouterState({ select: (s) => s.location.pathname });
  const me = useSession();
  const items = [["Practices", "/admin/practices"], ["Plans & flags", "/admin/flags"], ["Methods", "/admin/methods"], ["Knowledge", "/admin/knowledge"], ["Providers", "/admin/providers"], ["Channels", "/admin/channels"], ["Audit", "/admin/audit"], ["Support access", "/admin/support"]] as const;
  return (
    <AppShell wide title={props.title} right={me?.user.name ?? "Super-admin"} nav={items.map(([label, href]) => ({ label, href, active: path.startsWith(href), icon: icons.admin }))} onNavigate={(href) => void navigate({ to: href })} railBrand={brand(`${en.app} · admin`)} railFooter={<RailFooter name="Super-admin" />} actions={<TopActions />}>
      {props.children}
    </AppShell>
  );
}

/** CSS media query as React state (layout decisions stay in CSS; this only gates data fetching / navigation behaviour). */
export function useMedia(query: string): boolean {
  const [m, setM] = useState(() => typeof window !== "undefined" && window.matchMedia(query).matches);
  useEffect(() => { const mq = window.matchMedia(query); const h = () => setM(mq.matches); h(); mq.addEventListener("change", h); return () => mq.removeEventListener("change", h); }, [query]);
  return m;
}
